import { proformApiQuery } from '@eliadmin/core/src/api/proform/proform-api';
import type { ProformInvoice } from '@eliadmin/core/src/api/proform/proform-types';
import { ProfileInvoiceStatus } from '@eliadmin/core/src/components/profile/profile-invoice-status';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@eliadmin/core/src/components/ui/dialog';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { formatPrice, roundPrice } from '@eliadmin/core/src/utils/price-utils';
import { downloadBase64File } from '@eliadmin/core/src/utils/proform-utils';
import { DialogTitle } from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import type { PropsWithChildren, ReactNode } from 'react';
import { LuDownload } from 'react-icons/lu';

type ProfileInvoiceDialogProps = {
  invoice: ProformInvoice;
};

export const getDruhDokladu = (druh: ProformInvoice['druh_dokladu']) => {
  switch (druh) {
    case '10':
      return 'Faktura';
    case '20':
      return 'Zálohová faktura';
    case '30':
      return 'Proforma';
    case '40':
      return 'Snížené plnění';
    case '50':
      return 'Zvýšené plnění';
    default:
      return 'Neznámý';
  }
};

export function ProfileInvoiceDialog(props: PropsWithChildren<ProfileInvoiceDialogProps>) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const customer = useCustomerStore(s => s.me!.customer);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const invoiceDoklad = useQuery({
    queryKey: ['ELI_GET_INVOICE', customer.email, props.invoice.doklad],
    queryFn: () =>
      proformApiQuery('ELI_GET_INVOICE', {
        e_mail: customer.email,
        doklad: props.invoice.doklad,
      }),
    enabled: false,
  });

  return (
    <Dialog>
      <DialogTrigger asChild>{props.children}</DialogTrigger>
      <DialogContent className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle className="text-left text-2xl font-bold">{t('text1229')}</DialogTitle>
        </DialogHeader>
        <Separator />

        <div className="flex flex-col gap-4">
          <InfoAttribute label={t('text1229')} value={props.invoice.doklad} />
          <InfoAttribute label={t('text1237')} value={getDruhDokladu(props.invoice.druh_dokladu)} />
          <InfoAttribute label={t('text31')} value={props.invoice.var_symbol} />
          <InfoAttribute
            label={t('text121')}
            value={new Date(props.invoice.dat_vyst).toLocaleDateString(locale)}
          />
          <InfoAttribute
            label={t('text1238')}
            value={new Date(props.invoice.dat_zd_pln).toLocaleDateString(locale)}
          />
          <InfoAttribute
            label={t('text123')}
            value={new Date(props.invoice.dat_spl).toLocaleDateString(locale)}
          />
          <InfoAttribute
            label={t('text1230')}
            value={formatPrice(
              roundPrice(props.invoice.devizy),
              props.invoice.mena ?? currency,
              currencySettings,
            )}
          />
          <InfoAttribute
            label={t('text1231')}
            value={formatPrice(
              roundPrice(props.invoice.uhrazeno_devizy),
              props.invoice.mena ?? currency,
              currencySettings,
            )}
          />
          <InfoAttribute
            label={t('text1232')}
            value={formatPrice(
              roundPrice(props.invoice.devizy_k_uhrade),
              props.invoice.mena ?? currency,
              currencySettings,
            )}
          />

          <InfoAttribute
            label={t('text419')}
            value={<ProfileInvoiceStatus invoice={props.invoice} />}
          />

          {String(props.invoice.attach) === '1' && (
            <Button
              isLoading={invoiceDoklad.isLoading}
              onClick={async () => {
                const result = await invoiceDoklad.refetch();
                if (result.data && result.data.root.invoice) {
                  const { file_name, att } = result.data.root.invoice;
                  downloadBase64File(att, file_name);
                }
              }}
              className="gap-2"
            >
              <LuDownload /> {t('text1227')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

type InfoAttributeProps = {
  label: string;
  value: ReactNode;
};
function InfoAttribute(props: InfoAttributeProps) {
  return (
    <div className="flex flex-row justify-between">
      <span className="text-sm font-bold text-muted-foreground">{props.label}</span>
      <span className="text-sm font-normal">{props.value}</span>
    </div>
  );
}
